import React from 'react';
import { Plus, X, UserPlus, Baby } from 'lucide-react';

export const COVERAGE_TYPES = [
  { v: 'whole_life', l: 'Whole Life' },
  { v: 'term', l: 'Term Life' },
  { v: 'final_expense', l: 'Final Expense' },
  { v: 'medicare', l: 'Medicare' },
  { v: 'iul', l: 'IUL' },
  { v: 'annuity', l: 'Annuity' },
  { v: 'health', l: 'Health & Supplemental' },
  { v: 'other', l: 'Other' },
];

export const RELATIONSHIPS = [
  { v: 'self', l: 'Self (owner is the insured)' },
  { v: 'spouse', l: 'Spouse' },
  { v: 'mother', l: 'Mother' },
  { v: 'father', l: 'Father' },
  { v: 'son', l: 'Son' },
  { v: 'daughter', l: 'Daughter' },
  { v: 'sister', l: 'Sister' },
  { v: 'brother', l: 'Brother' },
  { v: 'grandparent', l: 'Grandparent' },
  { v: 'grandchild', l: 'Grandchild' },
  { v: 'other', l: 'Other' },
];

export const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'];

export const emptyQuoteData = () => ({
  firstName: '', lastName: '', middleInitial: '', email: '', phone: '',
  streetAddress: '', city: '', state: '', zipCode: '',
  coverageType: '', coverageAmount: '', monthlyBudget: '', majorHealthIssues: '',
  primary: { firstName: '', lastName: '', middleInitial: '', relationship: 'self', relationshipOther: '', age: '', dob: '', gender: '', smoker: false, heightFt: '', heightIn: '', weightLbs: '' },
  spouse: null,
  children: [],
});

const emptySpouse = () => ({ firstName: '', lastName: '', middleInitial: '', age: '', dob: '', gender: '', smoker: false, heightFt: '', heightIn: '', weightLbs: '' });
const emptyChild = () => ({ firstName: '', lastName: '', middleInitial: '', age: '', dob: '' });

// Compute age from a YYYY-MM-DD date-of-birth string
export const calcAgeFromDob = (dob) => {
  if (!dob) return '';
  const b = new Date(dob);
  if (isNaN(b.getTime())) return '';
  const t = new Date();
  let a = t.getFullYear() - b.getFullYear();
  const m = t.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && t.getDate() < b.getDate())) a -= 1;
  return (a >= 0 && a < 130) ? String(a) : '';
};

const _quoteHasData = (q) => {
  if (!q) return false;
  const p = q.primary || {};
  return !!(q.coverageType || q.coverageAmount || q.monthlyBudget || q.majorHealthIssues ||
    p.age || p.dob || p.gender || p.heightFt || p.weightLbs || q.spouse || (q.children && q.children.length));
};

// Normalized list of quote requests for a lead (supports legacy single quoteDetails)
export const getQuotes = (lead) => {
  if (!lead) return [];
  if (Array.isArray(lead.quotes) && lead.quotes.length) return lead.quotes;
  return _quoteHasData(lead.quoteDetails) ? [lead.quoteDetails] : [];
};

export const quoteCount = (lead) => getQuotes(lead).length;
export const hasQuoteDetails = (lead) => getQuotes(lead).length > 0;

// Map a CRM lead + a specific quote object into the quote-form data shape.
// Pass quote = {} to prefill only contact info (for adding a brand-new quote).
export const leadToQuoteData = (lead, quote) => {
  const base = emptyQuoteData();
  const q = quote !== undefined ? (quote || {}) : (getQuotes(lead)[0] || {});
  return {
    ...base,
    firstName: lead.firstName || '',
    lastName: lead.lastName || '',
    middleInitial: lead.middleInitial || q.primary?.middleInitial || '',
    email: lead.email || '',
    phone: lead.phone || '',
    streetAddress: lead.streetAddress || '',
    city: lead.city || '',
    state: lead.state || '',
    zipCode: lead.zipCode || '',
    coverageType: q.coverageType || '',
    coverageAmount: q.coverageAmount || '',
    monthlyBudget: q.monthlyBudget || '',
    majorHealthIssues: q.majorHealthIssues || '',
    primary: { ...base.primary, ...(q.primary || {}) },
    spouse: q.spouse ? { ...emptySpouse(), ...q.spouse } : null,
    children: Array.isArray(q.children) ? q.children.map(c => ({ ...emptyChild(), ...c })) : [],
  };
};

// Build the payload sent to the backend
export const buildQuotePayload = (d) => {
  const isSelf = (d.primary?.relationship || 'self') === 'self';
  return {
    firstName: (d.firstName || '').trim(),
    lastName: (d.lastName || '').trim(),
    middleInitial: (d.middleInitial || '').trim(),
    email: (d.email || '').trim(),
    phone: (d.phone || '').trim(),
    streetAddress: (d.streetAddress || '').trim(),
    city: (d.city || '').trim(),
    state: (d.state || '').trim(),
    zipCode: (d.zipCode || '').trim(),
    quoteDetails: {
      coverageType: d.coverageType || '',
      coverageAmount: (d.coverageAmount || '').trim(),
      monthlyBudget: (d.monthlyBudget || '').trim(),
      majorHealthIssues: (d.majorHealthIssues || '').trim(),
      primary: {
        ...d.primary,
        firstName: isSelf ? (d.firstName || '').trim() : (d.primary.firstName || '').trim(),
        lastName: isSelf ? (d.lastName || '').trim() : (d.primary.lastName || '').trim(),
        middleInitial: isSelf ? (d.middleInitial || '').trim() : (d.primary.middleInitial || '').trim(),
      },
      spouse: d.spouse,
      children: d.children,
    },
  };
};

const inputCls = 'w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-blue focus:border-brand-blue focus:outline-none transition';
const labelCls = 'block text-xs font-semibold text-gray-600 mb-1';

const AgeDobFields = ({ person, onChange, testid }) => {
  const handleDob = (v) => {
    const a = calcAgeFromDob(v);
    onChange(a ? { dob: v, age: a } : { dob: v });
  };
  return (
    <div>
      <label className={labelCls}>Date of Birth &amp; Age <span className="font-normal text-gray-400">(enter either — DOB auto-fills age)</span></label>
      <div className="flex gap-2">
        <input type="date" value={person.dob || ''} onChange={(e) => handleDob(e.target.value)} className={inputCls} data-testid={`${testid}-dob`} />
        <input type="number" min="0" max="120" value={person.age || ''} onChange={(e) => onChange({ age: e.target.value })} placeholder="Age" className={`${inputCls} max-w-[100px]`} data-testid={`${testid}-age`} />
      </div>
    </div>
  );
};

const GenderPicker = ({ value, onChange, testid }) => (
  <div>
    <label className={labelCls}>Gender</label>
    <div className="flex gap-2">
      {['male', 'female'].map(g => (
        <button key={g} type="button" onClick={() => onChange(g)}
          className={`flex-1 px-3 py-2.5 text-sm font-medium rounded-lg border transition ${value === g ? 'bg-brand-blue text-white border-brand-blue' : 'bg-white text-gray-700 border-gray-300 hover:border-brand-blue'}`}
          data-testid={`${testid}-${g}`}>
          {g === 'male' ? 'Male' : 'Female'}
        </button>
      ))}
    </div>
  </div>
);

const HeightField = ({ person, setter, testid }) => (
  <div>
    <label className={labelCls}>Height</label>
    <div className="flex gap-2">
      <select value={person.heightFt || ''} onChange={(e) => setter('heightFt', e.target.value)} className={inputCls} data-testid={`${testid}-ft`}>
        <option value="">ft</option>{[3, 4, 5, 6, 7].map(f => <option key={f} value={f}>{f} ft</option>)}
      </select>
      <select value={person.heightIn || ''} onChange={(e) => setter('heightIn', e.target.value)} className={inputCls} data-testid={`${testid}-in`}>
        <option value="">in</option>{Array.from({ length: 12 }, (_, i) => <option key={i} value={i}>{i} in</option>)}
      </select>
    </div>
  </div>
);

const WeightField = ({ person, setter, testid }) => (
  <div>
    <label className={labelCls}>Weight (lbs)</label>
    <input type="number" min="0" value={person.weightLbs || ''} onChange={(e) => setter('weightLbs', e.target.value)} placeholder="lbs" className={inputCls} data-testid={`${testid}-weight`} />
  </div>
);

const SmokerPicker = ({ value, onChange, testid }) => (
  <div>
    <label className={labelCls}>Smoker / Tobacco Use</label>
    <div className="flex gap-2">
      {[{ v: false, l: 'No' }, { v: true, l: 'Yes' }].map(o => (
        <button key={o.l} type="button" onClick={() => onChange(o.v)}
          className={`flex-1 px-3 py-2.5 text-sm font-medium rounded-lg border transition ${value === o.v ? 'bg-brand-blue text-white border-brand-blue' : 'bg-white text-gray-700 border-gray-300 hover:border-brand-blue'}`}
          data-testid={`${testid}-${o.l.toLowerCase()}`}>
          {o.l}
        </button>
      ))}
    </div>
  </div>
);

const AdvancedQuoteFields = ({ data, setData }) => {
  const setField = (f, v) => setData({ ...data, [f]: v });
  const setPrimary = (f, v) => setData({ ...data, primary: { ...data.primary, [f]: v } });
  const mergePrimary = (obj) => setData({ ...data, primary: { ...data.primary, ...obj } });
  const setSpouse = (f, v) => setData({ ...data, spouse: { ...data.spouse, [f]: v } });
  const mergeSpouse = (obj) => setData({ ...data, spouse: { ...data.spouse, ...obj } });
  const addSpouse = () => setData({ ...data, spouse: emptySpouse() });
  const removeSpouse = () => setData({ ...data, spouse: null });
  const addChild = () => setData({ ...data, children: [...data.children, emptyChild()] });
  const removeChild = (i) => setData({ ...data, children: data.children.filter((_, idx) => idx !== i) });
  const setChild = (i, f, v) => setData({ ...data, children: data.children.map((c, idx) => idx === i ? { ...c, [f]: v } : c) });
  const mergeChild = (i, obj) => setData({ ...data, children: data.children.map((c, idx) => idx === i ? { ...c, ...obj } : c) });

  return (
    <div className="space-y-6">
      {/* Contact / Primary Insured */}
      <section className="bg-white rounded-xl border border-gray-200 p-5" data-testid="section-contact">
        <h3 className="text-base font-bold text-brand-blue mb-4">Your Information</h3>
        <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
          <div className="sm:col-span-3"><label className={labelCls}>First Name *</label><input value={data.firstName} onChange={(e) => setField('firstName', e.target.value)} className={inputCls} data-testid="q-firstName" /></div>
          <div className="sm:col-span-2"><label className={labelCls}>Last Name *</label><input value={data.lastName} onChange={(e) => setField('lastName', e.target.value)} className={inputCls} data-testid="q-lastName" /></div>
          <div className="sm:col-span-1"><label className={labelCls}>MI</label><input maxLength={2} value={data.middleInitial} onChange={(e) => setField('middleInitial', e.target.value)} className={inputCls} data-testid="q-mi" /></div>
          <div className="sm:col-span-3"><label className={labelCls}>Email *</label><input type="email" value={data.email} onChange={(e) => setField('email', e.target.value)} className={inputCls} data-testid="q-email" /></div>
          <div className="sm:col-span-3"><label className={labelCls}>Phone *</label><input value={data.phone} onChange={(e) => setField('phone', e.target.value)} className={inputCls} data-testid="q-phone" /></div>
          <div className="sm:col-span-6"><label className={labelCls}>Street Address <span className="font-normal text-gray-400">(optional)</span></label><input value={data.streetAddress} onChange={(e) => setField('streetAddress', e.target.value)} className={inputCls} data-testid="q-street" /></div>
          <div className="sm:col-span-3"><label className={labelCls}>City <span className="font-normal text-gray-400">(optional)</span></label><input value={data.city} onChange={(e) => setField('city', e.target.value)} className={inputCls} data-testid="q-city" /></div>
          <div className="sm:col-span-1"><label className={labelCls}>State *</label>
            <select value={data.state} onChange={(e) => setField('state', e.target.value)} className={inputCls} data-testid="q-state">
              <option value="">--</option>
              {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2"><label className={labelCls}>ZIP Code *</label><input value={data.zipCode} onChange={(e) => setField('zipCode', e.target.value)} className={inputCls} data-testid="q-zip" /></div>
        </div>
      </section>

      {/* Coverage interest */}
      <section className="bg-white rounded-xl border border-gray-200 p-5" data-testid="section-coverage">
        <h3 className="text-base font-bold text-brand-blue mb-4">Coverage Interest</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div><label className={labelCls}>Coverage Type</label>
            <select value={data.coverageType} onChange={(e) => setField('coverageType', e.target.value)} className={inputCls} data-testid="q-coverageType">
              <option value="">Select coverage type…</option>
              {COVERAGE_TYPES.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
            </select>
          </div>
          <div><label className={labelCls}>Desired Coverage Amount</label><input value={data.coverageAmount} onChange={(e) => setField('coverageAmount', e.target.value)} placeholder="e.g. $25,000 / $50,000 / $100,000" className={inputCls} data-testid="q-coverageAmount" /></div>
          <div><label className={labelCls}>Estimated Monthly Budget</label><input value={data.monthlyBudget} onChange={(e) => setField('monthlyBudget', e.target.value)} placeholder="e.g. $50/mo / $100/mo / $150/mo" className={inputCls} data-testid="q-monthlyBudget" /></div>
        </div>
      </section>

      {/* Primary insured health details */}
      <section className="bg-white rounded-xl border border-gray-200 p-5" data-testid="section-primary">
        <h3 className="text-base font-bold text-brand-blue mb-1">Primary Insured Details</h3>
        <p className="text-xs text-gray-500 mb-4">Who is being insured? If it's someone other than the owner (e.g. a parent or sibling), choose the relationship and enter their name.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Relationship to Owner</label>
            <select value={data.primary.relationship || 'self'} onChange={(e) => setPrimary('relationship', e.target.value)} className={inputCls} data-testid="q-primary-relationship">
              {RELATIONSHIPS.map(r => <option key={r.v} value={r.v}>{r.l}</option>)}
            </select>
          </div>
          {data.primary.relationship === 'other' && (
            <div>
              <label className={labelCls}>Specify Relationship</label>
              <input value={data.primary.relationshipOther || ''} onChange={(e) => setPrimary('relationshipOther', e.target.value)} placeholder="e.g. Business partner, Fiancé" className={inputCls} data-testid="q-primary-relationship-other" />
            </div>
          )}
          {(data.primary.relationship || 'self') === 'self' ? (
            <div className="sm:col-span-2 text-sm text-gray-600 bg-gray-50 rounded-lg p-3" data-testid="q-primary-self-note">
              Insured is the owner: <b>{`${data.firstName || ''} ${data.middleInitial || ''} ${data.lastName || ''}`.replace(/\s+/g, ' ').trim() || '—'}</b>
            </div>
          ) : (
            <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-6 gap-3">
              <div className="sm:col-span-3"><label className={labelCls}>Insured First Name</label><input value={data.primary.firstName || ''} onChange={(e) => setPrimary('firstName', e.target.value)} className={inputCls} data-testid="q-primary-firstName" /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Insured Last Name</label><input value={data.primary.lastName || ''} onChange={(e) => setPrimary('lastName', e.target.value)} className={inputCls} data-testid="q-primary-lastName" /></div>
              <div className="sm:col-span-1"><label className={labelCls}>MI</label><input maxLength={2} value={data.primary.middleInitial || ''} onChange={(e) => setPrimary('middleInitial', e.target.value)} className={inputCls} data-testid="q-primary-mi" /></div>
            </div>
          )}
          <div className="sm:col-span-2"><AgeDobFields person={data.primary} onChange={mergePrimary} testid="q-primary" /></div>
          <HeightField person={data.primary} setter={setPrimary} testid="q-primary" />
          <WeightField person={data.primary} setter={setPrimary} testid="q-primary" />
          <GenderPicker value={data.primary.gender} onChange={(v) => setPrimary('gender', v)} testid="q-primary-gender" />
          <SmokerPicker value={data.primary.smoker} onChange={(v) => setPrimary('smoker', v)} testid="q-primary-smoker" />
        </div>
      </section>

      {/* Major health issues */}
      <section className="bg-white rounded-xl border border-gray-200 p-5" data-testid="section-health">
        <h3 className="text-base font-bold text-brand-blue mb-1">Major Health Conditions</h3>
        <p className="text-xs text-gray-500 mb-3">
          Please list any major health conditions (e.g., stroke, heart attack, COPD, cancer, kidney disease, diabetes).
          Major health conditions can affect your premium and eligibility. Leave blank if none.
        </p>
        <textarea rows={3} value={data.majorHealthIssues} onChange={(e) => setField('majorHealthIssues', e.target.value)}
          placeholder="List any major health conditions or medications…" className={inputCls} data-testid="q-health" />
      </section>

      {/* Spouse */}
      <section className="bg-white rounded-xl border border-gray-200 p-5" data-testid="section-spouse">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold text-brand-blue">Spouse to be Insured <span className="font-normal text-gray-400 text-sm">(optional)</span></h3>
          {data.spouse
            ? <button type="button" onClick={removeSpouse} className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1" data-testid="q-remove-spouse"><X size={15} /> Remove</button>
            : <button type="button" onClick={addSpouse} className="text-sm text-brand-blue font-semibold hover:text-brand-orange flex items-center gap-1" data-testid="q-add-spouse"><UserPlus size={15} /> Add Spouse</button>}
        </div>
        {data.spouse && (
          <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
            <div className="sm:col-span-3"><label className={labelCls}>First Name</label><input value={data.spouse.firstName} onChange={(e) => setSpouse('firstName', e.target.value)} className={inputCls} data-testid="q-spouse-firstName" /></div>
            <div className="sm:col-span-2"><label className={labelCls}>Last Name</label><input value={data.spouse.lastName} onChange={(e) => setSpouse('lastName', e.target.value)} className={inputCls} data-testid="q-spouse-lastName" /></div>
            <div className="sm:col-span-1"><label className={labelCls}>MI</label><input maxLength={2} value={data.spouse.middleInitial} onChange={(e) => setSpouse('middleInitial', e.target.value)} className={inputCls} data-testid="q-spouse-mi" /></div>
            <div className="sm:col-span-4"><AgeDobFields person={data.spouse} onChange={mergeSpouse} testid="q-spouse" /></div>
            <div className="sm:col-span-2"><GenderPicker value={data.spouse.gender} onChange={(v) => setSpouse('gender', v)} testid="q-spouse-gender" /></div>
            <div className="sm:col-span-2"><HeightField person={data.spouse} setter={setSpouse} testid="q-spouse" /></div>
            <div className="sm:col-span-2"><WeightField person={data.spouse} setter={setSpouse} testid="q-spouse" /></div>
            <div className="sm:col-span-2"><SmokerPicker value={data.spouse.smoker} onChange={(v) => setSpouse('smoker', v)} testid="q-spouse-smoker" /></div>
          </div>
        )}
      </section>

      {/* Children */}
      <section className="bg-white rounded-xl border border-gray-200 p-5" data-testid="section-children">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold text-brand-blue">Children to be Insured <span className="font-normal text-gray-400 text-sm">(optional)</span></h3>
          <button type="button" onClick={addChild} className="text-sm text-brand-blue font-semibold hover:text-brand-orange flex items-center gap-1" data-testid="q-add-child"><Baby size={15} /> Add Child</button>
        </div>
        {data.children.length === 0 && <p className="text-xs text-gray-400">No children added.</p>}
        <div className="space-y-3">
          {data.children.map((c, i) => (
            <div key={i} className="border border-gray-200 rounded-lg p-3 space-y-2" data-testid={`q-child-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-gray-600">Child {i + 1}</span>
                <button type="button" onClick={() => removeChild(i)} className="text-red-600 hover:text-red-700 flex items-center gap-1 text-xs" data-testid={`q-child-${i}-remove`}><X size={14} /> Remove</button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
                <div className="sm:col-span-3"><label className={labelCls}>First Name</label><input value={c.firstName} onChange={(e) => setChild(i, 'firstName', e.target.value)} className={inputCls} data-testid={`q-child-${i}-firstName`} /></div>
                <div className="sm:col-span-2"><label className={labelCls}>Last Name</label><input value={c.lastName} onChange={(e) => setChild(i, 'lastName', e.target.value)} className={inputCls} data-testid={`q-child-${i}-lastName`} /></div>
                <div className="sm:col-span-1"><label className={labelCls}>MI</label><input maxLength={2} value={c.middleInitial} onChange={(e) => setChild(i, 'middleInitial', e.target.value)} className={inputCls} data-testid={`q-child-${i}-mi`} /></div>
                <div className="sm:col-span-6"><AgeDobFields person={c} onChange={(obj) => mergeChild(i, obj)} testid={`q-child-${i}`} /></div>
              </div>
            </div>
          ))}
        </div>
        {data.children.length > 0 && (
          <button type="button" onClick={addChild} className="mt-3 text-sm text-brand-blue font-semibold hover:text-brand-orange flex items-center gap-1" data-testid="q-add-another-child"><Plus size={15} /> Add Another Child</button>
        )}
      </section>
    </div>
  );
};

export default AdvancedQuoteFields;
